/* <go-delivery-dates>: rewrites the plain delivery estimate on product cards
   ("Voraussichtliche Lieferzeit 2-3 Werktage") into a date line, same on mobile
   and desktop: "Lieferung Sa 24. - Di 27. Okt." (Delivery Sat 24 - Tue 27 Oct) (in stock, latest
   date under 7 days away) or "Lieferung 6. - 20. Nov." (weeks). In stock also unhides the
   card's in-stock badge ([data-go-in-stock-badge] inside [data-go-delivery-scope]).
   Date logic and format are copied from the PDP cutoff line in
   snippets/product-info.liquid (FAT-254); keep the two in sync.
   Rendered by snippets/go-delivery-dates.liquid, DE market only. */

const TZ = 'Europe/Amsterdam';
const DAY = 86400000;

function parseTime(str, fallback) {
  const p = (str || '').split(':');
  if (p.length !== 2) return fallback;
  return [+p[0] || 0, +p[1] || 0];
}

function amsParts(date) {
  const parts = {};
  new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .formatToParts(date)
    .forEach((p) => {
      parts[p.type] = p.value;
    });
  return parts;
}

// "2-3 Werktage" -> {min: 2, max: 3, unit: 'workdays'}. Returns null when no
// number + day/week unit is found (e.g. "morgen in huis"), so the plain text stays.
function parseDelivery(text) {
  const msg = String(text).toLowerCase();
  let unit = null;
  if (/werktag|werkdag|lavorativ|ouvr|business/.test(msg)) unit = 'workdays';
  else if (/woche|weken|week|semaine|settiman/.test(msg)) unit = 'weeks';
  else if (/tage|tag\b|dagen|\bdag\b|day|jour|giorn/.test(msg)) unit = 'days';
  if (!unit) return null;
  const range = msg.match(/(\d+)\s*[-–—]\s*(\d+)/);
  if (range) return { min: +range[1], max: +range[2], unit };
  const single = msg.match(/\d+/);
  if (single) return { min: +single[0], max: +single[0], unit };
  return null;
}

// Mon-Thu orders before the weekday cutoff and Friday orders before the Friday
// cutoff are fulfilled same day; everything else (incl. Sat/Sun) counts from the
// next fulfillment day (Mon-Fri). UTC-noon timestamps are immune to DST shifts.
function fulfillment(weekdayCutoff, fridayCutoff) {
  const p = amsParts(new Date());
  const todayTs = Date.UTC(+p.year, +p.month - 1, +p.day, 12, 0, 0);
  let ts = todayTs;
  const dow = new Date(ts).getUTCDay();
  const cut = dow === 5 ? fridayCutoff : weekdayCutoff;
  const h = p.hour === '24' ? 0 : +p.hour;
  const beforeCutoff = h * 60 + +p.minute < cut[0] * 60 + cut[1];
  const isWorkday = dow >= 1 && dow <= 5;
  if (!isWorkday || !beforeCutoff) {
    ts += DAY;
    while (true) {
      const d = new Date(ts).getUTCDay();
      if (d === 0 || d === 6) ts += DAY;
      else break;
    }
  }
  return { todayTs, startTs: ts };
}

// Saturday counts as a delivery day (German "Werktag" is Mon-Sat); only Sunday is skipped.
function addWorkdays(ts, n) {
  let added = 0;
  while (added < n) {
    ts += DAY;
    if (new Date(ts).getUTCDay() !== 0) added += 1;
  }
  return ts;
}

// In stock = latest delivery date less than 7 calendar days from today (ECH-76, call
// 2026-10-23): those lines show weekdays and the card shows the in-stock badge.
// Counted on real dates, so "5-7 Werktage" over a weekend does not qualify.
const IN_STOCK_DAYS = 7;

function isInStock(todayTs, toTs) {
  return (toTs - todayTs) / DAY < IN_STOCK_DAYS;
}

// Kaufland style date range, short month (Tom, Slack 2026-10-23) once at the end when both dates share it:
// de "Sa 24. - Di 27. Okt.", "Fr 30. Okt. - Mi 4. Nov.", "6. - 20. Nov.".
// Intl keeps each language's order; commas and the dot after a 2 letter weekday are dropped
// ("Sa., 24." -> "Sa 24.", so the line fits one row on mobile cards) and the en dash
// becomes a spaced ASCII hyphen (design rule: no en/em dashes in UI copy).
function formatDates(fromTs, toTs, lang, withWeekday) {
  const fmt = new Intl.DateTimeFormat(lang, {
    weekday: withWeekday ? 'short' : undefined,
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  const from = new Date(fromTs);
  const to = new Date(toTs);
  let text;
  if (fromTs === toTs) text = fmt.format(from);
  // formatRange adds the year to both dates when it changes ("30. Dez. 2026 - 2. Jan. 2027")
  else text = fmt.formatRange(from, to);
  return text.replace(/\s*[–—]\s*/g, ' - ').replace(/,/g, '').replace(/\b([A-Z][a-z])\./g, '$1');
}

class GoDeliveryDates extends HTMLElement {
  connectedCallback() {
    const lang = this.dataset.lang || 'de';
    const tpl = this.dataset.tpl || '';
    const { todayTs, startTs } = fulfillment(
      parseTime(this.dataset.cutoffTime, [23, 59]),
      parseTime(this.dataset.cutoffTimeFriday, [17, 0])
    );

    this.querySelectorAll('.delivery-text').forEach((el) => {
      // Rewritten text can re-match parseDelivery ("Freitag" -> "tag"), so only run once.
      if ('goDeliveryDates' in el.dataset) return;
      const spec = parseDelivery(el.textContent);
      if (!spec) return;
      el.dataset.goDeliveryDates = '';
      let from;
      let to;
      if (spec.unit === 'weeks') {
        from = startTs + spec.min * 7 * DAY;
        to = startTs + spec.max * 7 * DAY;
      } else if (spec.unit === 'workdays') {
        from = addWorkdays(startTs, spec.min);
        to = addWorkdays(startTs, spec.max);
      } else {
        from = startTs + spec.min * DAY;
        to = startTs + spec.max * DAY;
      }
      const inStock = isInStock(todayTs, to);
      const dates = document.createElement('span');
      dates.className = 'go-delivery-dates__date';
      dates.textContent = formatDates(from, to, lang, inStock);
      const line = document.createElement('span');
      line.className = 'go-delivery-dates__line';
      // Translation string stays text nodes (never parsed as HTML).
      tpl.split('[[DATES]]').forEach((chunk, i) => {
        if (i > 0) line.append(dates);
        if (chunk) line.append(chunk);
      });
      el.replaceChildren(line);

      // Hidden lines belong to other variants (snippets/inventory.liquid); the badge follows the shown one,
      // and never shows for a sold-out card (data-available, the card's selected or first available variant).
      if (inStock && this.dataset.available === 'true' && !el.closest('[hidden]')) {
        const badge = this.closest('[data-go-delivery-scope]')?.querySelector('[data-go-in-stock-badge]');
        if (badge) badge.hidden = false;
      }
    });
  }
}

if (!customElements.get('go-delivery-dates')) {
  customElements.define('go-delivery-dates', GoDeliveryDates);
}
